import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator, CommandShortcut,
} from './ui/command';
import {
  LayoutDashboard, Activity, Truck, Mail, CheckSquare, Users, Building2,
  CreditCard, Receipt, Calculator, TrendingUp, DollarSign, FileSpreadsheet,
  ClipboardCheck, BarChart3, HeartPulse, Settings, Bell, Sparkles,
  AlertTriangle, Clock, ArrowRightLeft, Send, Brain, Trophy, MailX,
  FileSearch, Copy, ShieldCheck, Flag, Network, FileText, Plug, FileUp,
  Tag, CalendarClock, ClipboardList, Banknote,
} from 'lucide-react';

/**
 * ⌘K / Ctrl+K command palette. Mounted once by <Layout>; keeps its own
 * open/close state and intercepts the global keyboard shortcut. Every
 * top-level route in the app should have an entry here — power users
 * expect two keystrokes to reach anywhere.
 *
 * Not shown in the login flow — the palette relies on `<Layout>` being
 * mounted, which only happens for authenticated users.
 */

// Route registry — grouped for the palette; the order below is also the
// visual order in the palette when the search box is empty.
const ROUTES = [
  // Operations
  { path: '/', label: 'Dashboard', group: 'Operations', icon: LayoutDashboard, keywords: 'home overview' },
  { path: '/workflow', label: 'Action Center', group: 'Operations', icon: Activity, keywords: 'alerts todo workflow' },
  { path: '/loads', label: 'Loads', group: 'Operations', icon: Truck, keywords: 'shipments freight' },
  { path: '/opportunities', label: 'Opportunities', group: 'Operations', icon: Mail, keywords: 'inbound emails parsed' },

  // CRM
  { path: '/tasks', label: 'Tasks Dashboard', group: 'CRM', icon: CheckSquare, keywords: 'todo activities' },
  { path: '/leads', label: 'Leads', group: 'CRM', icon: Users, keywords: 'prospects sales' },
  { path: '/customers', label: 'Customers', group: 'CRM', icon: Building2, keywords: 'shippers' },
  { path: '/carriers', label: 'Carriers', group: 'CRM', icon: Truck, keywords: 'trucking companies' },
  { path: '/credit-requests', label: 'Credit Requests', group: 'CRM', icon: CreditCard, keywords: 'credit application' },
  { path: '/admin/email-hub', label: 'Email Hub', group: 'CRM', icon: Mail, keywords: 'email templates inbox' },
  { path: '/sequences', label: 'Sequences', group: 'CRM', icon: Send, keywords: 'automation drip email' },
  { path: '/email-templates', label: 'Email Templates', group: 'CRM', icon: Mail, keywords: 'template canned' },
  { path: '/email-analytics', label: 'Email Analytics', group: 'CRM', icon: BarChart3, keywords: 'opens clicks stats' },
  { path: '/admin/campaigns', label: 'Campaigns', group: 'CRM', icon: Sparkles, keywords: 'bulk email blast' },
  { path: '/ai-hub', label: 'AI Command Center', group: 'CRM', icon: Brain, keywords: 'ai gpt suggest' },
  { path: '/leaderboard', label: 'Leaderboard', group: 'CRM', icon: Trophy, keywords: 'sales rep ranking' },

  // Finance
  { path: '/invoices', label: 'Invoices', group: 'Finance', icon: Receipt, keywords: 'billing ar receivables' },
  { path: '/accounting', label: 'Accounting', group: 'Finance', icon: Calculator, keywords: 'books' },
  { path: '/accounting/profit-aggregations', label: 'Margins', group: 'Finance', icon: TrendingUp, keywords: 'profit margin aggregations' },
  { path: '/accounting/insights', label: 'Insights Hub', group: 'Finance', icon: Sparkles, keywords: 'insights ai finance' },
  { path: '/accounting/aging', label: 'Aging (AR/AP)', group: 'Finance', icon: AlertTriangle, keywords: 'aging overdue receivables' },
  { path: '/accounting/aged-wip', label: 'Un-invoiced Loads (WIP)', group: 'Finance', icon: Clock, keywords: 'wip un-invoiced pending' },
  { path: '/accounting/billing-emails', label: 'Billing Emails', group: 'Finance', icon: Mail, keywords: 'billing reminders dunning' },
  { path: '/accounting/ap-reconciliation', label: 'AP Reconciliation', group: 'Finance', icon: ArrowRightLeft, keywords: 'ap reconcile match' },
  { path: '/commissions', label: 'Commissions', group: 'Finance', icon: DollarSign, keywords: 'sales commission payout' },
  { path: '/finance/payments', label: 'Payments Register', group: 'Finance', icon: Banknote, keywords: 'payments received collected paid cash mtd last month' },
  { path: '/finance/reports-exports', label: 'Reports & Exports', group: 'Finance', icon: FileSpreadsheet, keywords: 'quickbooks 1099 export' },
  { path: '/accounting/quickbooks-export', label: 'QuickBooks Export', group: 'Finance', icon: FileSpreadsheet, keywords: 'quickbooks iif export' },
  { path: '/accounting/1099-nec', label: '1099-NEC', group: 'Finance', icon: FileSpreadsheet, keywords: '1099 nec tax' },
  { path: '/factoring-companies', label: 'Factoring Companies', group: 'Finance', icon: Building2, keywords: 'factoring finance' },

  // Reports
  { path: '/reports', label: 'Reports', group: 'Reports', icon: BarChart3, keywords: 'dashboards analytics' },
  { path: '/activity-reports', label: 'Activity Reports', group: 'Reports', icon: ClipboardList, keywords: 'sales activity daily' },
  { path: '/scheduled-reports', label: 'Scheduled Reports', group: 'Reports', icon: CalendarClock, keywords: 'scheduled cron report' },

  // Admin
  { path: '/approvals', label: 'Approvals', group: 'Admin', icon: ClipboardCheck, keywords: 'approve pending' },
  { path: '/users', label: 'Users', group: 'Admin', icon: Users, keywords: 'team members' },
  { path: '/admin/system-health', label: 'System Health', group: 'Admin', icon: HeartPulse, keywords: 'health ops audit' },
  { path: '/admin/loads-audit', label: 'Loads Audit', group: 'Admin', icon: FileSearch, keywords: 'audit log timeline' },
  { path: '/duplicate-loads', label: 'Duplicate Loads', group: 'Admin', icon: Copy, keywords: 'duplicates merge' },
  { path: '/admin/customer-duplicates', label: 'Duplicate Customers', group: 'Admin', icon: Copy, keywords: 'duplicates merge customers ein' },
  { path: '/admin/sales-rep-health', label: 'Sales Rep Health', group: 'Admin', icon: HeartPulse, keywords: 'rep audit ops' },
  { path: '/admin/credit-risk', label: 'Credit Risk (DSO)', group: 'Admin', icon: ShieldCheck, keywords: 'dso credit risk' },
  { path: '/admin/disputes', label: 'Open Disputes', group: 'Admin', icon: Flag, keywords: 'dispute chargeback' },
  { path: '/admin/mailgun-suppressions', label: 'Mailgun Suppressions', group: 'Admin', icon: MailX, keywords: 'bounces unsubscribes' },
  { path: '/offices', label: 'Offices', group: 'Admin', icon: Network, keywords: 'branch office location' },
  { path: '/document-templates', label: 'Document Templates', group: 'Admin', icon: FileText, keywords: 'pdf template document' },
  { path: '/integrations', label: 'Integrations', group: 'Admin', icon: Plug, keywords: 'api integration mailgun stripe' },
  { path: '/data-import', label: 'Data Import', group: 'Admin', icon: FileUp, keywords: 'import csv upload' },
  { path: '/admin/entity-categories', label: 'Entity Categories', group: 'Admin', icon: Tag, keywords: 'tags categories client broker' },
  { path: '/admin/grace-periods', label: 'Grace Periods', group: 'Admin', icon: ShieldCheck, keywords: 'grace payment' },
  { path: '/settings', label: 'Settings', group: 'Admin', icon: Settings, keywords: 'preferences config' },
  { path: '/notifications', label: 'Notifications', group: 'Admin', icon: Bell, keywords: 'alerts inbox notifications' },
];

// Deduplicate by group order + preserve insertion for identical-group items.
const GROUPED = ROUTES.reduce((acc, r) => {
  if (!acc[r.group]) acc[r.group] = [];
  acc[r.group].push(r);
  return acc;
}, {});
const GROUP_ORDER = ['Operations', 'CRM', 'Finance', 'Reports', 'Admin'];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Global ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const jump = useCallback((path) => {
    setOpen(false);
    navigate(path);
  }, [navigate]);

  const groupsToRender = useMemo(() => GROUP_ORDER.filter((g) => GROUPED[g]?.length), []);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Type a page name, or search…"
        data-testid="command-palette-input"
      />
      <CommandList data-testid="command-palette-list">
        <CommandEmpty>No matching page found.</CommandEmpty>
        {groupsToRender.map((groupName, idx) => (
          <React.Fragment key={groupName}>
            {idx > 0 && <CommandSeparator />}
            <CommandGroup heading={groupName}>
              {GROUPED[groupName].map((r) => (
                <CommandItem
                  key={r.path}
                  value={`${r.label} ${r.keywords || ''} ${r.group}`}
                  onSelect={() => jump(r.path)}
                  data-testid={`command-palette-item-${r.path.replace(/\W+/g, '-').replace(/^-|-$/g, '')}`}
                >
                  <r.icon className="mr-2 h-4 w-4 text-slate-500" />
                  <span>{r.label}</span>
                  <CommandShortcut className="text-[10px] text-slate-400">{r.path}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </React.Fragment>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
